<script setup lang="ts">
// DESIGN.md section 9.1, screen 1 — idle Home. Table drawing + "Start match" CTA (one-shot bo3
// rapid11, see Round-30 below; "Otras opciones" is the secondary link into the full config screen).
//
// The right-rail rankings ticker + last-3-results panel from the original design was removed
// per explicit feedback (2026-08-30): on the real 960x540 kiosk it read as a whole extra
// sidebar/"bar" competing with the CTA, and it fully duplicates content one tap away on the
// Rankings and Past matches tabs — so Home stays a single focused column. A badge scan here is
// handled globally (src/composables/useGlobalScanner.ts) and jumps straight to New match with
// that player left — that scan-on-Home behavior is unchanged by the below.
//
// 2026-08-31 (user feedback): Home is now the new-game editor directly, not just a static
// illustration. It drives the SAME shared `draft` store New match reads (src/store/draft.ts —
// already module-level exactly so both screens can write to one place); New match's own
// click-a-side/"Pick a name" flow is untouched. Tapping anywhere on a side (the whole side
// region, including the small "Select" pill under the "?" placeholder — TableDrawing.vue) opens
// the same PlayerSearchList dialog New match uses, scoped to that side. Neither the CTA nor a
// completing pick resets the draft (that would wipe picks just made here); the draft is instead
// cleared once a match is actually created (startMatch()/NewMatch.vue's start(), right after
// navigating to Live) so Home always starts blank the next time a match finishes.
//
// Round-30 (2026-08-31, Issue 2): filling the 2nd side used to auto-advance straight to New
// match (no separate "continue" tap) — that auto-jump is GONE now that Home's own CTA is a
// one-shot Start (below): advancing away the instant both sides fill would defeat the point of
// a same-screen fast path. Badge-scan-on-Home keeps its own, different, pre-existing behavior
// unchanged (useGlobalScanner.ts navigates to New match on the FIRST scan, filling the left side
// — that's a distinct, already-documented flow, not the tap-to-pick one this change touches).
import { computed, ref } from 'vue'
import TableDrawing from '../components/TableDrawing.vue'
import PlayerSearchList from '../components/PlayerSearchList.vue'
import MicPulse from '../components/MicPulse.vue'
import LiveMatchBanner from '../components/LiveMatchBanner.vue'
import { navigate } from '../store/nav'
import { draft, resetDraft, setSide, swapDraftSides } from '../store/draft'
import { settings } from '../store/settings'
import { stt } from '../stt'
import { tableStore } from '../store/table'
import { createMatch, ApiRequestError } from '../backend'
import { MATCH_TYPE_SERVE_EVERY_DEFAULTS } from '../types'
import { t } from '../i18n'
import type { Player, Side } from '../types'

// Round-23 (2026-08-31 live-testing fix): "the Home mic should visibly pulse with live audio
// strength" — the header's MicButton pulse is subtle by design (it lives in a tight corner next
// to the clock/gear); Home has the room for a bigger, unmissable instance of the SAME MicPulse
// component so a speaker gets clear visual proof the table is hearing them at normal distance,
// right where they're about to say "i play on the red/blue side". Tap toggles the SAME
// voiceCommandsEnabled setting as the header button (store/settings.ts) — one source of truth,
// two surfaces, exactly like MicButton and SettingsPanel already share it.
//
// Round-24 (2026-08-31, user feedback): bumped from a small size-40 secondary link to a
// prominent, touch-friendly primary affordance (this is the kiosk's main screen, on a
// touchscreen — it needs to read as a real control, not a footnote), and moved below the table
// block instead of below the "New match" CTA, per explicit placement feedback.
//
// Round-26 (2026-08-31, user feedback): "must be a CIRCLE, and pulse much more" — the full-width
// rectangle affordance is gone. MicPulse.vue now renders its own filled round button (see that
// file), so this screen just wraps it in a plain, background-less circular tap target at ~2x the
// old halo's size (56 -> 112) and keeps the status line as a caption underneath instead of
// packed into the same row — the CTA below stays the visually dominant control, this reads as a
// clearly-tappable assistant orb next to it.
//
// Round-30 (2026-08-31, Issue 1 + Issue 2 fixes):
//  - Issue 1: a live match already sitting on this table (a forgotten/unfinished earlier game)
//    used to be invisible until the player hit "table already has a live match" trying to start
//    a new one. LiveMatchBanner.vue (reads the same SSE-driven tableStore this screen already
//    listens to indirectly) now surfaces it up top with Resume/Finalizar actions, so the state is
//    never a dead end — abandoning frees the table (server flips liveMatch to null, banner
//    disappears) in one confirm tap, unblocking Start right after.
//  - Issue 2: the primary CTA is now a genuine ONE-SHOT start — tapping it with both sides filled
//    immediately creates a best-of-3 rapid11 (11-point) match via createMatch, forced regardless
//    of whatever format/best-of was last used (draft.matchTypeCode/bestOf/serveEvery are left
//    untouched so the persisted "last format" still drives the advanced NewMatch screen). The
//    longer configuration path (classic 21, bo5, serve settings) survives as "Otras opciones",
//    a secondary link into the existing NewMatch flow — same navigate('newMatch') the old
//    full-width CTA used to do. firstServer/serveEvery on the fast path reuse the SAME draft
//    fields NewMatch's Start already sends (draft.firstServer is kept current by
//    refreshFirstServerSuggestion in store/draft.ts on every pick/swap — "whoever served first
//    less"; serveEvery is hardcoded to MATCH_TYPE_SERVE_EVERY_DEFAULTS.rapid11 = 2, the ITTF
//    standard, matching what picking "Rapid 11" on NewMatch would also resolve to).
const micState = computed<'off' | 'broken' | 'live'>(() => {
  if (!settings.voiceCommandsEnabled) return 'off'
  if (!stt.connected) return 'broken'
  return 'live'
})
function toggleVoice() {
  settings.voiceCommandsEnabled = !settings.voiceCommandsEnabled
}

const dialogSide = ref<Side | null>(null)
const pickError = ref<string | null>(null)

function openPicker(side: Side) {
  draft.activeSide = side
  dialogSide.value = side
}

function onPick(player: Player) {
  const side = dialogSide.value
  if (!side) return
  const otherSide: Side = side === 'left' ? 'right' : 'left'
  if (draft[otherSide]?.id === player.id) {
    pickError.value = `${player.displayName} is already on the other side`
    setTimeout(() => (pickError.value = null), 4000)
    dialogSide.value = null
    return
  }
  setSide(side, player)
  dialogSide.value = null
}

// Round-30 (Issue 1): the same live match LiveMatchBanner shows also gates the fast-path Start
// below — no point letting the tap round-trip to the server just to get the table_busy error
// back when the state is already known client-side.
const liveMatch = computed(() => tableStore.state?.liveMatch ?? null)
const sameSide = computed(() => draft.left && draft.right && draft.left.id === draft.right.id)
const bothFilled = computed(() => !!draft.left && !!draft.right && !sameSide.value)
const canStart = computed(() => bothFilled.value && !liveMatch.value)

const starting = ref(false)
const startError = ref<string | null>(null)

// Round-30 (Issue 2): "start match should just begin a bo3 11pt match" — one tap, no format/
// best-of screen. matchTypeCode/bestOf/serveEvery are hardcoded here (NOT read from draft, which
// only tracks the LAST-USED format for the advanced NewMatch path — see store/draft.ts) so this
// fast path always means the same thing regardless of what was previously configured.
async function startMatch() {
  if (!canStart.value || !draft.left || !draft.right || starting.value) return
  starting.value = true
  startError.value = null
  try {
    const match = await createMatch({
      matchTypeCode: 'rapid11',
      bestOf: 3,
      leftPlayerId: draft.left.id,
      rightPlayerId: draft.right.id,
      firstServer: draft.firstServer,
      serveEvery: MATCH_TYPE_SERVE_EVERY_DEFAULTS.rapid11,
      table: tableStore.table,
    })
    resetDraft()
    navigate('live', { matchId: match.id })
  } catch (e) {
    if (e instanceof ApiRequestError) {
      if (e.code === 'table_busy') startError.value = 'This table already has a live match'
      else if (e.code === 'same_player') startError.value = 'Both sides have the same player'
      else startError.value = e.message
    } else {
      startError.value = 'Could not start the match'
    }
  } finally {
    starting.value = false
  }
}

// The longer configuration path (classic 21, bo5, serve settings) — same NewMatch screen as
// before, just reached from a secondary affordance now instead of being the only option.
function openAdvancedOptions() {
  navigate('newMatch')
}
</script>

<template>
  <div class="flex-1 min-h-0 flex flex-col gap-4 p-4 items-center">
    <div class="w-full max-w-xl flex-1 min-h-0 flex flex-col justify-center gap-4">
      <!-- Issue 1: a live match already on this table is never invisible again — shows up here
           with Resume/Finalizar the moment tableStore's SSE state has one, on Home specifically
           since that's where the old "table already has a live match" error used to dead-end. -->
      <LiveMatchBanner />

      <TableDrawing
        :left-player="draft.left"
        :right-player="draft.right"
        interactive
        selectable
        show-swap
        corner-theme
        @select-side="openPicker"
        @swap="swapDraftSides"
        @clear-side="(side) => setSide(side, null)"
      />

      <!-- Round-26 (2026-08-31, user feedback): "must be a CIRCLE, and pulse much more" —
           replaces the round-24 full-width rectangle. MicPulse now IS the visible round button
           (filled circle + halo, see that file); this wrapper is just a plain hit-target sized
           to match, ~2x the old halo (56 -> 112). Status text moved to a caption below instead
           of living inside the button row. -->
      <div class="flex flex-col items-center gap-2">
        <button
          type="button"
          class="tap-target rounded-full flex items-center justify-center"
          @click="toggleVoice"
        >
          <MicPulse :level="stt.level" :state="micState" :size="112" />
        </button>
        <span
          class="text-sm font-semibold text-center"
          :class="{
            'text-neutral-500': micState === 'off',
            'text-red-400': micState === 'broken',
            'text-emerald-300': micState === 'live',
          }"
        >
          {{ micState === 'off' ? 'Voice off — tap to enable' : micState === 'broken' ? 'Voice: reconnecting…' : 'Listening — try "I play on the blue side"' }}
        </span>
      </div>

      <!-- Issue 2: one-shot Start — both sides filled starts a bo3 rapid11 match immediately,
           no format screen. Disabled (not a dead click) when incomplete or when a live match
           already occupies the table (LiveMatchBanner above owns that state). -->
      <button
        type="button"
        class="tap-target rounded-2xl bg-emerald-600 active:bg-emerald-500 text-2xl font-bold py-4 shadow-lg disabled:opacity-40"
        :disabled="!canStart || starting"
        @click="startMatch"
      >
        {{ starting ? 'Starting…' : 'Start match' }}
      </button>
      <button
        type="button"
        class="text-center text-neutral-400 text-sm font-semibold underline underline-offset-2"
        @click="openAdvancedOptions"
      >
        Otras opciones (formato, mejor de X, saque)
      </button>

      <p v-if="pickError" class="text-center text-red-400 font-semibold text-sm">{{ pickError }}</p>
      <p v-else-if="startError" class="text-center text-red-400 font-semibold text-sm">{{ startError }}</p>
      <p v-else-if="!bothFilled && !liveMatch" class="text-center text-neutral-500 text-sm">{{ t('pickBothPlayersToStart') }} — scan your badge or tap a side</p>
    </div>

    <PlayerSearchList v-if="dialogSide" @pick="onPick" @close="dialogSide = null" />
  </div>
</template>
